import { createPortal } from 'react-dom';
import { Dashboard } from '.';
import { PanelContainer, TLoadedPanelProps } from '../types';
import { importPanel } from '../util';
import { EventEmitter, parseXmlAsync, toBoolean, useMount } from '@apia/util';
import { ReactPortal, useState } from 'react';
import { PanelErrorBoundary } from '../splash/panels/PanelErrorBoundary';
import { PanelIdentifier } from '../splash/panels/PanelIdentifier';
import { firePanelAction } from '../actions/firePanelAction';
import { parametersStore, saveParameters } from '../config';
import { DashboardPanelContext } from './DashboardPanelContext';
import {
  TActionDispatcherOptions,
  TActionResult,
} from '../actions/usePanelActions';

const noData = { initialData: {} };

type TPanelProps = Omit<TLoadedPanelProps, 'initialData'>;
function parameterValue(value: string | boolean | number) {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return value;
}

export class DashboardPanel extends EventEmitter<{
  hasLoaded: boolean;
  isVisible: boolean;
}> {
  dashboard: Dashboard;
  hasInited = false;
  hasLoaded = false;
  id: string;
  initialData: Promise<unknown> | null = null;
  isVisible = false;
  portal: ReactPortal;
  props: TPanelProps = {} as TPanelProps;

  constructor({
    id,
    dashboard,
    PanelContainer,
  }: {
    id: string;
    dashboard: Dashboard;
    PanelContainer: PanelContainer;
  }) {
    super();

    this.dashboard = dashboard;
    this.id = id;
    this.#loaders.loadAll();

    this.portal = createPortal(
      <this.PanelElement
        ActualPanelContainer={PanelContainer}
        Element={importPanel(this.props.type)}
      />,
      this.getContainer() as HTMLElement,
      this.id,
    );
  }

  #elements = {
    props: () => {
      const panelData = document.querySelector(
        `panel-props[id="${this.id as string}"]`,
      ) as HTMLElement | null;

      if (!panelData) {
        console.error('Panel with no id:', this.id);
        throw new Error('A panel has no data element in the DOM.');
      }

      return panelData;
    },
  };

  #loaders = {
    loadAll: () => {
      void this.#loaders.initialData();
      this.#loaders.parameters();
      this.#loaders.props();
    },
    initialData: () => {
      const panelContent = this.getContainer();
      const xml = panelContent.innerHTML
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&amp;', '&');
      panelContent.innerHTML = '';

      this.initialData = parseXmlAsync(xml.replace(/<!--?[^>]+>/, ''));

      return this.initialData;
    },
    parameters: () => {
      if (parametersStore.getState().panelParameters[this.id]) return;

      const panelData = this.#elements.props();

      const options = Object.fromEntries(
        [...panelData.querySelectorAll<HTMLElement>('panel-option')].map(
          (current) => {
            return [
              current.getAttribute('name') as string,
              parameterValue(current.getAttribute('value') as string),
            ];
          },
        ),
      );

      const parameters = Object.fromEntries(
        [...panelData.querySelectorAll<HTMLElement>('panel-parameter')].map(
          (current) => {
            return [
              current.getAttribute('name') as string,
              parameterValue(current.getAttribute('value') as string),
            ];
          },
        ),
      );

      saveParameters(this.id, Object.assign(options, parameters), false);
    },
    props: () => {
      const panelData = this.#elements.props();
      const panel = this.getContainer();

      const props: Omit<TLoadedPanelProps, 'initialData'> = {
        id: this.id,
        canMove: toBoolean(panelData.getAttribute('canMove')),
        hasRss: toBoolean(panelData.getAttribute('hasRss')),
        image: panelData.getAttribute('imagePath') as string,
        isConfigurable: toBoolean(panelData.getAttribute('isConfigurable')),
        isRefreshable: toBoolean(panelData.getAttribute('isRefreshable')),
        name: panelData.getAttribute('name') as string,
        refreshOnHome: toBoolean(panelData.getAttribute('refreshOnHome')),
        refreshOnStart: toBoolean(panelData.getAttribute('refreshOnStart')),
        showTitle: toBoolean(panelData.getAttribute('showTitle')),
        title: panelData.getAttribute('title') as string,
        type: panel.dataset.panel as string,
      };

      this.props = props;
    },
  };

  fireAction: (
    { action, ...parameters }: { action: string } & Record<string, unknown>,
    options?: TActionDispatcherOptions,
  ) => Promise<TActionResult> = ({ action, ...parameters }, options) => {
    if (!this.isVisible) return new Promise((resolve) => resolve(null));

    const postData: Record<string, unknown> = {};

    Object.entries(parameters).forEach(([name, value]) => {
      postData[`p_${name}`] = value;
    });

    return new Promise<TActionResult>((resolve, reject) => {
      firePanelAction(this.id, action, postData, {
        ...options,
        onComplete(hasSucceed, data) {
          options?.onComplete?.(hasSucceed, data);
          if (hasSucceed) {
            resolve(data);
          } else reject();
        },
      });
    });
  };

  getContainer() {
    const panel = document.querySelector(
      `[data-id="${this.id}"][data-panel]`,
    ) as HTMLElement;

    if (!panel) {
      console.error('Panel not found in dom:', this.id);
      throw new Error('Panel not found in DOM.');
    }

    return panel;
  }

  getParameters() {
    return parametersStore.getState().panelParameters[this.id];
  }

  hide() {
    const container = this.getContainer();
    container.style.display = 'none';
    container.ariaHidden = 'true';

    this.isVisible = false;
    this.emit('isVisible', this.isVisible);
  }

  refresh() {
    return this.fireAction({ action: 'refresh' });
  }

  setHasLoaded() {
    this.hasLoaded = true;
    this.emit('hasLoaded', true);
  }

  show() {
    const container = this.getContainer();
    container.style.display = 'block';
    container.ariaHidden = 'false';

    this.hasInited = true;
    this.isVisible = true;
    this.emit('isVisible', this.isVisible);
  }

  PanelElement = ({
    ActualPanelContainer,
    Element,
  }: {
    ActualPanelContainer: PanelContainer;
    Element: ReturnType<typeof importPanel>;
  }) => {
    const [resolvedInitialData, setResolvedInitialData] = useState<{
      initialData: unknown;
    } | null>(null);
    const [isVisible, setIsVisible] = useState(this.isVisible);

    useMount(() => {
      void this.initialData?.then((data) => {
        setResolvedInitialData((data ?? noData) as { initialData: unknown });
      });

      return this.on('isVisible', (ev) =>
        setIsVisible((current) => ev || current),
      );
    });

    if (resolvedInitialData === null || resolvedInitialData === noData)
      return null;

    return (
      <DashboardPanelContext.Provider value={this}>
        <PanelIdentifier {...this.props}>
          <ActualPanelContainer
            {...this.props}
            initialData={resolvedInitialData}
          >
            <this.dashboard.bootstrapper.SuspendPanelUntilDashbordReady
              panelId={this.id}
            >
              <PanelErrorBoundary>
                {isVisible ? (
                  <Element initialData={resolvedInitialData} />
                ) : null}
              </PanelErrorBoundary>
            </this.dashboard.bootstrapper.SuspendPanelUntilDashbordReady>
          </ActualPanelContainer>
        </PanelIdentifier>
      </DashboardPanelContext.Provider>
    );
  };
}
